import {
  Component,
  ChangeDetectionStrategy,
  input,
  computed,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RvBadgeComponent } from '../../../components/rv-badge/rv-badge.component';

export type OnlineStatusValue =
  | 'online'
  | 'offline'
  | 'standby'
  | 'not_activated';

type StatusBadgeColor =
  | 'green'
  | 'sky'
  | 'red'
  | 'amber'
  | 'blue'
  | 'orange'
  | 'gray';

interface DisplayStatusBadgeRow {
  trackId: string;
  color: StatusBadgeColor;
  text: string;
  icon: string | null;
  tooltip?: string | null;
}

export const ScreenStatus = {
  ON: 'ON',
  OFF_EXPECTED: 'OFF - EXPECTED',
  OFF_UNEXPECTED: 'OFF - UNEXPECTED',
  UNSUPPORTED: '',
} as const;

@Component({
  selector: 'display-status',
  templateUrl: './display-status.component.html',
  standalone: true,
  imports: [MatTooltipModule, RvBadgeComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DisplayStatusComponent {
  onlineStatus = input.required<OnlineStatusValue>();
  screenPowerStatus = input<
    (typeof ScreenStatus)[keyof typeof ScreenStatus] | null
  >();
  badgeShape = input<'chip' | 'icon'>('icon');

  statusBadges = computed(() => {
    const chip = this.badgeShape() === 'chip';
    const badges: DisplayStatusBadgeRow[] = [];
    const currentOnline = this.onlineStatus();
    const currentScreenPower = this.screenPowerStatus();

    switch (currentOnline) {
      case 'online':
        if (
          currentScreenPower !== ScreenStatus.OFF_EXPECTED &&
          currentScreenPower !== ScreenStatus.OFF_UNEXPECTED
        ) {
          badges.push({
            trackId: 'online',
            color: 'green',
            text: chip ? 'Online' : '',
            icon: chip ? null : 'status-badge-online',
            tooltip: 'Online',
          });
        }
        break;
      case 'standby':
        badges.push({
          trackId: 'standby',
          color: 'sky',
          text: chip ? 'Standby' : '',
          icon: chip ? null : 'status-badge-standby',
          tooltip: 'Standby',
        });
        break;
      case 'offline':
        badges.push({
          trackId: 'offline',
          color: 'red',
          text: chip ? 'Offline' : '',
          icon: chip ? null : 'status-badge-offline',
          tooltip: 'Offline',
        });
        break;
      case 'not_activated':
        badges.push({
          trackId: 'not_activated',
          color: 'gray',
          text: chip ? 'Not Activated' : '',
          icon: chip ? null : 'status-badge-not-activated',
          tooltip: 'Not Activated',
        });
        break;
    }

    if (currentOnline !== 'offline' && currentOnline !== 'not_activated') {
      if (currentScreenPower === ScreenStatus.OFF_EXPECTED) {
        badges.push({
          trackId: 'off_expected',
          color: 'blue',
          text: chip ? 'Off (expected)' : '',
          icon: chip ? null : 'status-badge-off-expected',
          tooltip: 'Scheduled Off',
        });
      } else if (currentScreenPower === ScreenStatus.OFF_UNEXPECTED) {
        badges.push({
          trackId: 'off_unexpected',
          color: 'orange',
          text: chip ? 'Off (unexpected)' : '',
          icon: chip ? null : 'status-badge-off-unexpected',
          tooltip: 'Unexpected Off',
        });
      }
    }

    return badges;
  });
}
